/// <reference lib="dom" />

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Calendar,
  ChevronDown,
  ExternalLink,
  MapPin,
  Smartphone,
  ThumbsDown,
  ThumbsUp,
  Ticket,
  Users,
} from "lucide-react";
import { id } from "@instantdb/react";
import type { EventEntry } from "../../../src/fields.ts";
import { type Category, categoryEmoji, cats } from "../../../src/category.ts";
import { api } from "../api.ts";
import { eventPriceText } from "../eventUtils.ts";
import { Spinner } from "./Spinner.tsx";
import { EventMap } from "./EventMap.tsx";
import { PhoneVerification } from "./PhoneVerification.tsx";
import { db } from "../db.ts";
import { Label, Select } from "./ui/index.ts";

const isPhoneVerificationError = (message: string) =>
  message.toLowerCase().includes("phone verification");

type Country = "Israel" | "US" | "Germany";

const countries: { value: Country | ""; label: string }[] = [
  { value: "", label: "All Countries" },
  { value: "Israel", label: "Israel" },
  { value: "US", label: "US" },
  { value: "Germany", label: "Germany" },
];

export const validTimeFilters = [
  "next-30-days",
  "today",
  "tomorrow",
  "this-week",
  "this-weekend",
] as const;

export type TimeFilter = typeof validTimeFilters[number];

const timeFilterMap: Record<string, TimeFilter> = {
  today: "today",
  tomorrow: "tomorrow",
  "this-week": "this-week",
  week: "this-week",
  "this-weekend": "this-weekend",
  weekend: "this-weekend",
  "next-30-days": "next-30-days",
  month: "next-30-days",
};

export const parseTimeFilter = (val: string | null): TimeFilter =>
  val ? (timeFilterMap[val.toLowerCase()] ?? "next-30-days") : "next-30-days";

const countryMap: Record<string, Country | ""> = {
  all: "",
  "": "",
  israel: "Israel",
  il: "Israel",
  us: "US",
  usa: "US",
  germany: "Germany",
  de: "Germany",
};

export const parseCountry = (val: string | null): Country | "" | null =>
  val === null ? null : (countryMap[val.toLowerCase()] ?? null);

export const normalizeCategory = (raw: string | null): string => {
  if (!raw) return "";
  if (raw in cats) return raw;
  const decoded = decodeURIComponent(raw).trim().toLowerCase();
  if (decoded in cats) return decoded;
  const withSpaces = decoded.replace(/-/g, " ");
  if (withSpaces in cats) return withSpaces;
  const withHyphens = decoded.replace(/\s+/g, "-");
  return withHyphens in cats ? withHyphens : "";
};

const toQueryDatetime = (date: Date) => date.toISOString();

const formatEventDate = (start: number, end?: number) => {
  const startDate = new Date(start);
  const startStr = startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  if (!end || end <= start) return startStr;
  const endDate = new Date(end);
  const isDiffDay = startDate.getDate() !== endDate.getDate() ||
    startDate.getMonth() !== endDate.getMonth() ||
    startDate.getFullYear() !== endDate.getFullYear();
  if (!isDiffDay) return startStr;
  const endStr = endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `${startStr} – ${endStr}`;
};

const EventRow = (
  { event, onSwipe }: {
    event: EventEntry;
    onSwipe: (direction: "like" | "dislike") => void;
  },
) => {
  const navigate = useNavigate();
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [feedback, setFeedback] = useState<"like" | "dislike" | null>(null);
  const [imageError, setImageError] = useState(false);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const isSwipingRef = useRef<boolean | null>(null);
  const hasMovedRef = useRef(false);

  const startDate = new Date(event.structured.timestamp.start);
  const month = startDate.toLocaleDateString("en-US", { month: "short" });
  const day = startDate.getDate();
  const weekday = startDate.toLocaleDateString("en-US", { weekday: "short" });
  const hasBanner = Boolean(event.structured.posterUrl && !imageError);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    isSwipingRef.current = null;
    hasMovedRef.current = false;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    if (isSwipingRef.current === null) {
      if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
        hasMovedRef.current = true;
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          isSwipingRef.current = true;
        } else {
          isSwipingRef.current = false;
        }
      }
    }

    if (isSwipingRef.current === true) {
      if (e.cancelable) {
        e.preventDefault();
      }
      setTranslateX(deltaX);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (isSwipingRef.current === true) {
      const threshold = 120;
      if (translateX > threshold) {
        setTranslateX(globalThis.innerWidth);
        setTimeout(() => {
          onSwipe("like");
        }, 150);
      } else if (translateX < -threshold) {
        setTranslateX(-globalThis.innerWidth);
        setTimeout(() => {
          onSwipe("dislike");
        }, 150);
      } else {
        setTranslateX(0);
      }
    } else {
      setTranslateX(0);
    }
    isSwipingRef.current = null;
  };

  const handleClick = () => {
    if (hasMovedRef.current || isDragging || translateX !== 0) return;
    navigate(`/event/${event.id}`);
  };

  const priceText = eventPriceText(
    "en",
    event.structured.categories as Category[],
    event.structured.priceRange,
    event.structured.country,
  );

  return (
    <div className="relative overflow-hidden rounded-2xl mb-3 select-none">
      {/* Swipe Background Reveal */}
      {translateX !== 0 && (
        <div
          className={`absolute inset-0 flex items-center justify-between px-6 rounded-2xl transition-colors duration-200 ${
            translateX > 0 ? "bg-green-500/20" : "bg-red-500/20"
          }`}
        >
          {/* Left side (Like, visible when swiping right) */}
          <div
            className={`flex items-center gap-2 text-green-400 font-semibold transition-opacity duration-150 ${
              translateX > 0 ? "opacity-100" : "opacity-0"
            }`}
            style={{
              transform: `scale(${
                Math.min(1.2, Math.max(0.8, translateX / 100))
              })`,
            }}
          >
            <ThumbsUp size={24} />
            <span className="text-sm">Save</span>
          </div>

          {/* Right side (Dislike, visible when swiping left) */}
          <div
            className={`flex items-center gap-2 text-red-400 font-semibold transition-opacity duration-150 ${
              translateX < 0 ? "opacity-100" : "opacity-0"
            }`}
            style={{
              transform: `scale(${
                Math.min(1.2, Math.max(0.8, -translateX / 100))
              })`,
            }}
          >
            <span className="text-sm">Pass</span>
            <ThumbsDown size={24} />
          </div>
        </div>
      )}

      {/* Actual Card Content */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleClick();
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: feedback === "like"
            ? "translateX(50px)"
            : feedback === "dislike"
            ? "translateX(-50px)"
            : `translateX(${translateX}px)`,
          opacity: feedback ? 0 : 1,
        }}
        className={`group relative flex gap-3.5 sm:gap-4 items-start bg-[#161616]/80 hover:bg-[#1f1f1f]/90 border border-white/[0.08] hover:border-purple-500/30 p-3 sm:p-4 rounded-2xl cursor-pointer transition-all duration-300 ease-in-out shadow-sm hover:shadow-xl hover:shadow-purple-500/5 ${
          isDragging ? "transition-none" : ""
        } ${
          feedback === "like"
            ? "bg-green-500/20 border-green-500/30 scale-95"
            : feedback === "dislike"
            ? "bg-red-500/20 border-red-500/30 scale-95"
            : ""
        }`}
      >
        {/* Banner / Poster / Date Thumbnail */}
        {hasBanner
          ? (
            <div className="relative w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 rounded-xl overflow-hidden shrink-0 bg-black/40 border border-white/10 shadow-sm flex items-center justify-center">
              <div
                className="absolute inset-0 bg-cover bg-center blur-md opacity-40 scale-110 pointer-events-none"
                style={{
                  backgroundImage: `url(${event.structured.posterUrl})`,
                }}
                aria-hidden="true"
              />
              <img
                src={event.structured.posterUrl}
                alt=""
                loading="lazy"
                onError={() => setImageError(true)}
                className="relative z-10 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300 ease-out"
              />
              {/* Overlaid Date Chip */}
              <div className="absolute top-1.5 start-1.5 z-20 px-1.5 py-0.5 rounded-md bg-black/75 backdrop-blur-md border border-white/15 text-center shadow-md select-none">
                <div className="text-[9px] font-bold text-purple-300 uppercase leading-none tracking-wider">
                  {month}
                </div>
                <div className="text-xs sm:text-sm font-black text-white leading-none mt-0.5">
                  {day}
                </div>
              </div>
              {event.structured.soldOut && (
                <div className="absolute bottom-1.5 start-1.5 z-20 px-1.5 py-0.5 rounded-md bg-rose-500/90 backdrop-blur-sm text-[9px] font-bold text-white leading-none shadow">
                  Sold Out
                </div>
              )}
            </div>
          )
          : (
            <div className="relative w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 rounded-xl overflow-hidden shrink-0 bg-gradient-to-br from-purple-900/30 via-[#181226] to-black/60 border border-purple-500/20 shadow-sm flex flex-col items-center justify-center text-center p-2 group-hover:border-purple-500/40 transition-colors">
              <div className="text-[10px] sm:text-xs font-bold text-purple-400 uppercase tracking-wider">
                {month}
              </div>
              <div className="text-2xl sm:text-3xl font-black text-white tracking-tight my-0.5">
                {day}
              </div>
              <div className="text-[10px] text-gray-400 font-medium uppercase">
                {weekday}
              </div>
              {event.structured.soldOut && (
                <div className="mt-1 px-1.5 py-0.5 rounded-md bg-rose-500/80 text-[9px] font-bold text-white leading-none shadow">
                  Sold Out
                </div>
              )}
            </div>
          )}

        {/* Details Column */}
        <div className="min-w-0 flex-1 flex flex-col justify-between self-stretch">
          <div>
            {/* Title & Badges */}
            <div className="flex items-start gap-2 flex-wrap justify-between">
              <h3
                dir="auto"
                className="text-white font-bold text-base sm:text-lg leading-snug group-hover:text-purple-300 transition-colors line-clamp-2"
              >
                {event.structured.name}
              </h3>
            </div>

            {/* Badges Row */}
            {(event.recommendationScore !== undefined ||
              (event.interestedFriends && event.interestedFriends.length > 0) ||
              (!hasBanner && event.structured.soldOut)) && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                {event.interestedFriends &&
                  event.interestedFriends.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-purple-500/20 text-purple-200 border border-purple-500/35 px-2 py-0.5 rounded-full shrink-0">
                    <Users className="w-3 h-3 text-purple-300" />
                    {event.interestedFriends.join(", ")} interested
                  </span>
                )}
                {event.recommendationScore !== undefined && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-full shrink-0">
                    ✨ {Math.round(event.recommendationScore * 100)}% Match
                  </span>
                )}
                {!hasBanner && event.structured.soldOut && (
                  <span className="text-[10px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 px-2 py-0.5 rounded-full shrink-0">
                    Sold Out
                  </span>
                )}
              </div>
            )}

            {/* Meta (Date, Venue, Location, Price) */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-400 mt-1.5">
              <span className="flex items-center gap-1 text-gray-300">
                <Calendar className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                {formatEventDate(
                  event.structured.timestamp.start,
                  event.structured.timestamp.end,
                )}
              </span>
              {(event.structured.venue || event.structured.location ||
                event.structured.city) && (
                <span className="flex items-center gap-1 truncate max-w-[200px] sm:max-w-[280px]">
                  <MapPin className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <span className="truncate" dir="auto">
                    {[
                      event.structured.venue,
                      event.structured.city || event.structured.location,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </span>
              )}
              {priceText && (
                <span className="flex items-center gap-1 text-purple-300/90 font-medium">
                  <Ticket className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  {priceText}
                </span>
              )}
            </div>
          </div>

          {/* Categories & Actions Row */}
          <div className="flex items-center justify-between gap-2 mt-2.5 pt-1">
            {/* Category Chips */}
            <div className="flex flex-wrap gap-1 min-w-0">
              {event.structured.categories.slice(0, 3).map((cat) => {
                const emoji = categoryEmoji(cat as Category);
                return (
                  <span
                    key={cat}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/25 font-medium shrink-0"
                  >
                    {emoji ? `${emoji} ${cat}` : cat}
                  </span>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFeedback("like");
                  setTimeout(() => {
                    onSwipe("like");
                    setFeedback(null);
                  }, 300);
                }}
                className="hidden md:flex px-2.5 py-1 bg-green-500/10 hover:bg-green-500/20 border border-green-500/25 hover:border-green-500/40 text-green-400 hover:text-green-300 rounded-lg text-xs font-semibold tracking-wide transition-all active:scale-95 duration-150 items-center gap-1 shadow-sm"
                title="Save to My Events"
              >
                <ThumbsUp size={12} />
                <span>Save</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFeedback("dislike");
                  setTimeout(() => {
                    onSwipe("dislike");
                    setFeedback(null);
                  }, 300);
                }}
                className="hidden md:flex px-2.5 py-1 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 hover:border-red-500/40 text-red-400 hover:text-red-300 rounded-lg text-xs font-semibold tracking-wide transition-all active:scale-95 duration-150 items-center gap-1 shadow-sm"
                title="Pass"
              >
                <ThumbsDown size={12} />
                <span>Pass</span>
              </button>
              {event.structured.links.length > 0 && (
                <a
                  href={event.structured.links[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 hover:border-purple-500/40 text-purple-400 hover:text-purple-300 rounded-lg transition"
                  title="Go to Link"
                >
                  <ExternalLink size={13} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getTimeRange = (option: string): { start: Date; end: Date } => {
  const now = new Date();

  if (option === "today") {
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999,
    );
    return { start: now, end };
  }

  if (option === "tomorrow") {
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      0,
      0,
      0,
      0,
    );
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      23,
      59,
      59,
      999,
    );
    return { start, end };
  }

  if (option === "this-weekend") {
    const day = now.getDay();
    const fridayOffset = (5 - day + 7) % 7;
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + fridayOffset,
      0,
      0,
      0,
      0,
    );
    const sundayOffset = fridayOffset + 2;
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + sundayOffset,
      23,
      59,
      59,
      999,
    );

    if (day === 5 || day === 6 || day === 0) {
      const endOfWeekend = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + ((7 - day) % 7),
        23,
        59,
        59,
        999,
      );
      return { start: now, end: endOfWeekend };
    }

    return { start, end };
  }

  if (option === "this-week") {
    const day = now.getDay();
    const sundayOffset = (7 - day) % 7;
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + sundayOffset,
      23,
      59,
      59,
      999,
    );
    return { start: now, end };
  }

  const end = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 30,
    23,
    59,
    59,
    999,
  );
  return { start: now, end };
};

export const EventList = () => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [profileCountry, setProfileCountry] = useState<Country | "">("");
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [mapRadius, setMapRadius] = useState<number>(20);
  const [showMap, setShowMap] = useState<boolean>(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifyNonce, setVerifyNonce] = useState(0);
  const [swipedIds, setSwipedIds] = useState<Set<string>>(new Set());
  const [recommendedEvents, setRecommendedEvents] = useState<EventEntry[]>([]);
  const [swipedActions, setSwipedActions] = useState<
    Map<string, "like" | "dislike">
  >(new Map());
  const [likedPastExpanded, setLikedPastExpanded] = useState(false);
  const [dislikedExpanded, setDislikedExpanded] = useState(false);
  const [lastSwipe, setLastSwipe] = useState<
    {
      eventId: string;
      eventName: string;
      direction: "like" | "dislike";
      swipeId: string;
    } | null
  >(null);
  const [showToast, setShowToast] = useState(false);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const user = db.useAuth().user;
  const [profileLoaded, setProfileLoaded] = useState(!user?.id);

  const categoryFilter = normalizeCategory(
    searchParams.get("category") ?? searchParams.get("filter") ??
      searchParams.get("cat"),
  );
  const timeFilter = parseTimeFilter(
    searchParams.get("time") ?? searchParams.get("when"),
  );
  const countryParam = parseCountry(searchParams.get("country"));
  const country = countryParam !== null ? countryParam : profileCountry;
  const friendsOnly = searchParams.get("friends") === "true" ||
    searchParams.get("friends") === "1";
  const rawView = searchParams.get("view") ?? searchParams.get("viewMode");
  const viewMode: "all" | "swiped" = rawView === "swiped" || rawView === "saved"
    ? "swiped"
    : "all";

  const handleCategoryChange = (val: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        val ? next.set("category", val) : next.delete("category");
        next.delete("filter");
        next.delete("cat");
        return next;
      },
      { replace: true },
    );

  const handleTimeChange = (val: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        val && val !== "next-30-days"
          ? next.set("time", val)
          : next.delete("time");
        next.delete("when");
        return next;
      },
      { replace: true },
    );

  const handleCountryChange = (val: Country | "") => {
    setMapCenter(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        val
          ? next.set("country", val)
          : profileCountry
          ? next.set("country", "all")
          : next.delete("country");
        return next;
      },
      { replace: true },
    );
  };

  const handleFriendsToggle = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        friendsOnly ? next.delete("friends") : next.set("friends", "true");
        return next;
      },
      { replace: true },
    );

  const handleViewModeChange = (mode: "all" | "swiped") =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mode === "swiped" ? next.set("view", "swiped") : next.delete("view");
        next.delete("viewMode");
        return next;
      },
      { replace: true },
    );

  useEffect(() => {
    if (!user?.id) {
      setProfileLoaded(true);
      return;
    }
    setProfileLoaded(false);
    api({
      endpoint: "getUserProfile",
      payload: {},
    })
      .then((profile) => {
        if (profile.coordinates) {
          const coords: [number, number] = [
            profile.coordinates[0],
            profile.coordinates[1],
          ];
          setMapCenter((prev) =>
            prev && prev[0] === coords[0] && prev[1] === coords[1]
              ? prev
              : coords
          );
        }
        if (profile.country) {
          const mappedCountry = profile.country === "USA"
            ? "US"
            : (profile.country as Country);
          setProfileCountry((prev) =>
            prev === mappedCountry ? prev : mappedCountry
          );
        }
      })
      .catch((err) => {
        console.error(
          "Failed to fetch user profile for default location:",
          err,
        );
      })
      .finally(() => {
        setProfileLoaded(true);
      });
  }, [user?.id]);
  const swipesQuery = db.useQuery(
    user
      ? {
        $users: {
          $: {
            where: { id: user.id },
          },
          swipes: {},
        },
      }
      : null,
  );

  const swipes = swipesQuery.data?.$users[0]?.swipes ?? [];
  const swipeMap = new Map(swipes.map((s) => [s.eventId, s.direction]));

  const getEventDirection = (
    eventId: string,
  ): "like" | "dislike" | undefined => {
    const dir = swipeMap.get(eventId);
    return dir === "like" || dir === "dislike"
      ? dir
      : swipedActions.get(eventId);
  };

  const hasSwiped = (eventId: string): boolean => {
    return swipeMap.has(eventId) || swipedIds.has(eventId);
  };

  const visibleRecommended = recommendedEvents.filter((e) => !hasSwiped(e.id));

  const allSavedEvents = [...events];
  for (const e of recommendedEvents) {
    if (hasSwiped(e.id) && !allSavedEvents.some((x) => x.id === e.id)) {
      allSavedEvents.push(e);
    }
  }

  const likedFuture = viewMode === "swiped"
    ? allSavedEvents.filter((e) =>
      getEventDirection(e.id) === "like" &&
      e.structured.timestamp.start >= Date.now()
    )
    : [];

  const likedPast = viewMode === "swiped"
    ? allSavedEvents.filter((e) =>
      getEventDirection(e.id) === "like" &&
      e.structured.timestamp.start < Date.now()
    )
    : [];

  const disliked = viewMode === "swiped"
    ? allSavedEvents.filter((e) => getEventDirection(e.id) === "dislike")
    : [];

  const handleSwipe = (eventId: string, direction: "like" | "dislike") => {
    if (!user) return;
    const matchedEvent = events.find((e) => e.id === eventId) ??
      recommendedEvents.find((e) => e.id === eventId);
    const eventName = matchedEvent?.structured.name ?? "Event";

    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    setSwipedActions((prev) => {
      const next = new Map(prev);
      next.set(eventId, direction);
      return next;
    });
    setSwipedIds((prev) => {
      const next = new Set(prev);
      next.add(eventId);
      return next;
    });

    const swipeId = id();

    setLastSwipe({
      eventId,
      eventName,
      direction,
      swipeId,
    });
    setShowToast(true);

    db.transact(
      db.tx.swipes[swipeId].create({
        eventId,
        direction,
        timestamp: Date.now(),
      }).link({ user: user.id }),
    );

    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
    }, 5000);
  };

  const handleUndoSwipe = () => {
    if (!lastSwipe || !user) return;

    setSwipedIds((prev) => {
      const next = new Set(prev);
      next.delete(lastSwipe.eventId);
      return next;
    });
    setSwipedActions((prev) => {
      const next = new Map(prev);
      next.delete(lastSwipe.eventId);
      return next;
    });

    db.transact(db.tx.swipes[lastSwipe.swipeId].delete());

    setShowToast(false);
    setLastSwipe(null);
  };

  const baseVisibleEvents = viewMode === "swiped"
    ? events
    : events.filter((e) => !hasSwiped(e.id));

  const friendsEventsCount = baseVisibleEvents.filter(
    (e) => e.interestedFriends && e.interestedFriends.length > 0,
  ).length;

  const visibleEvents = friendsOnly
    ? baseVisibleEvents.filter(
      (e) => e.interestedFriends && e.interestedFriends.length > 0,
    )
    : baseVisibleEvents;

  useEffect(() => {
    if (user?.id && countryParam === null && !profileLoaded) return;
    let active = true;

    setFetching(true);
    setError(null);

    if (viewMode === "swiped") {
      const start = new Date();
      const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);

      Promise.all([
        api({
          endpoint: "getUserSavedEvents",
          payload: {
            userId: user?.id,
          },
        }),
        api({
          endpoint: "getEvents",
          payload: {
            time: {
              startDatetime: toQueryDatetime(start),
              endDatetime: toQueryDatetime(end),
            },
            onlyStartedInRange: true,
            limit: 50,
            ...(country
              ? {
                country: (country === "US" ? "USA" : country) as
                  | "Israel"
                  | "Germany"
                  | "USA",
              }
              : {}),
            ...(mapCenter
              ? { coordinates: mapCenter, radiusKm: mapRadius }
              : {}),
            userId: user?.id,
          },
        }),
      ])
        .then(([swipedRes, unswipedRes]) => {
          if (!active) return;
          setEvents(swipedRes);

          const filteredRecommended = unswipedRes.filter(
            (e) =>
              (e.interestedFriends && e.interestedFriends.length > 0) ||
              (e.recommendationScore !== undefined &&
                e.recommendationScore >= 0.9),
          ).sort((a, b) => {
            const friendsA = a.interestedFriends?.length ?? 0;
            const friendsB = b.interestedFriends?.length ?? 0;
            if (friendsB !== friendsA) {
              return friendsB - friendsA;
            }
            return (b.recommendationScore ?? 0) - (a.recommendationScore ?? 0);
          });

          setRecommendedEvents(filteredRecommended);
        })
        .catch((err: unknown) => {
          if (!active) return;
          setError(
            err instanceof Error ? err.message : "Failed to load events",
          );
        })
        .finally(() => {
          if (active) setFetching(false);
        });
      return () => {
        active = false;
      };
    }

    const { start, end } = getTimeRange(timeFilter);

    api({
      endpoint: "getEvents",
      payload: {
        time: {
          startDatetime: toQueryDatetime(start),
          endDatetime: toQueryDatetime(end),
        },
        onlyStartedInRange: true,
        limit: 50,
        ...(country
          ? {
            country: (country === "US" ? "USA" : country) as
              | "Israel"
              | "Germany"
              | "USA",
          }
          : {}),
        ...(mapCenter ? { coordinates: mapCenter, radiusKm: mapRadius } : {}),
        ...(categoryFilter ? { categories: [categoryFilter] } : {}),
        userId: user?.id,
      },
    })
      .then((res) => {
        if (!active) return;
        const sorted = res.slice().sort((a, b) => {
          const friendsA = a.interestedFriends?.length ?? 0;
          const friendsB = b.interestedFriends?.length ?? 0;
          if (friendsB !== friendsA) {
            return friendsB - friendsA;
          }
          const scoreA = a.recommendationScore ?? 0;
          const scoreB = b.recommendationScore ?? 0;
          if (scoreB !== scoreA) {
            return scoreB - scoreA;
          }
          return a.structured.timestamp.start - b.structured.timestamp.start;
        });
        setEvents(sorted);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Failed to load events");
      })
      .finally(() => {
        if (active) setFetching(false);
      });

    return () => {
      active = false;
    };
  }, [
    profileLoaded,
    viewMode,
    country,
    mapCenter?.[0],
    mapCenter?.[1],
    mapRadius,
    timeFilter,
    categoryFilter,
    user?.id,
    verifyNonce,
  ]);

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      {/* View Mode Toggle */}
      <div className="flex gap-2 p-1 bg-white/5 border border-white/10 rounded-xl mb-6 max-w-xs">
        <button
          type="button"
          onClick={() => handleViewModeChange("all")}
          className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all ${
            viewMode === "all"
              ? "bg-purple-600 text-white shadow-md shadow-purple-500/10"
              : "text-gray-400 hover:text-white"
          }`}
        >
          All Events
        </button>
        <button
          type="button"
          onClick={() => handleViewModeChange("swiped")}
          className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all ${
            viewMode === "swiped"
              ? "bg-purple-600 text-white shadow-md shadow-purple-500/10"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Your Events
        </button>
      </div>

      {/* Controls */}
      {viewMode === "all" && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mb-6 bg-white/5 border border-white/10 rounded-2xl p-4">
          {/* Country Filter */}
          {!showMap && (
            <div className="flex items-center gap-2">
              <Label htmlFor="country-filter">Country</Label>
              <Select
                id="country-filter"
                size="sm"
                value={country}
                onChange={(e) =>
                  handleCountryChange(e.currentTarget.value as Country | "")}
                options={countries}
              />
            </div>
          )}

          {/* Time Filter */}
          <div className="flex items-center gap-2">
            <Label htmlFor="time-filter">When</Label>
            <Select
              id="time-filter"
              size="sm"
              value={timeFilter}
              onChange={(e) => handleTimeChange(e.currentTarget.value)}
              options={[
                { value: "next-30-days", label: "Next 30 Days" },
                { value: "today", label: "Today" },
                { value: "tomorrow", label: "Tomorrow" },
                { value: "this-week", label: "This Week" },
                { value: "this-weekend", label: "This Weekend" },
              ]}
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Label htmlFor="category-filter">Category</Label>
            <Select
              id="category-filter"
              size="sm"
              value={categoryFilter}
              onChange={(e) => handleCategoryChange(e.currentTarget.value)}
              className="max-w-[170px] truncate"
            >
              <option value="">All Categories</option>
              {Object.entries(cats).map(([id, cat]) => (
                <option key={id} value={id}>
                  {cat.emoji} {cat.display.en}
                </option>
              ))}
            </Select>
          </div>

          {/* Toggle Map Button */}
          <button
            type="button"
            onClick={() => {
              setShowMap(!showMap);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
              showMap
                ? "bg-purple-600/20 text-purple-300 border-purple-500/30 shadow-lg shadow-purple-500/5"
                : "text-gray-400 hover:text-white hover:bg-white/5 border-white/10"
            }`}
          >
            🗺️ {showMap ? "Hide Map" : "Show Map"}
          </button>

          {/* Friends Filter Toggle */}
          {friendsEventsCount > 0 && (
            <button
              type="button"
              onClick={handleFriendsToggle}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                friendsOnly
                  ? "bg-purple-600/30 text-purple-200 border-purple-500/50 shadow-lg shadow-purple-500/10 font-bold"
                  : "text-purple-300 hover:text-white bg-purple-500/10 hover:bg-purple-500/20 border-purple-500/30"
              }`}
            >
              <Users className="w-3.5 h-3.5 text-purple-300" />
              Friends ({friendsEventsCount})
            </button>
          )}

          <p
            className={`text-gray-500 text-sm ml-auto font-medium transition-opacity duration-150 ${
              fetching ? "opacity-0" : "opacity-100"
            }`}
          >
            {visibleEvents.length} events
          </p>
        </div>
      )}

      {viewMode === "all" && mapCenter && (
        <div className="flex items-center justify-between bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 mb-6 text-sm text-purple-200">
          <div className="flex items-center gap-2">
            <span>📍</span>
            <span>
              Filtering within <strong>{Math.round(mapRadius)}km</strong>{" "}
              of map location (
              {mapCenter[0].toFixed(3)}, {mapCenter[1].toFixed(3)})
            </span>
          </div>
          <button
            type="button"
            onClick={() => setMapCenter(null)}
            className="text-xs font-semibold px-2.5 py-1 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg border border-purple-500/30 transition text-purple-300"
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* Collapsible Interactive Map */}
      {viewMode === "all" && showMap && (
        <div className="mb-6">
          <EventMap
            events={visibleEvents}
            country={country}
            center={mapCenter}
            onBoundsChange={(coords, radius) => {
              setMapCenter(coords);
              setMapRadius(radius);
            }}
          />
        </div>
      )}

      {/* Events List / Loading / Error Container */}
      <div className="relative min-h-[200px]">
        {error && isPhoneVerificationError(error)
          ? (
            <div className="flex flex-col items-center gap-4 py-12 text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center">
                <Smartphone className="h-7 w-7 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Verify your phone
                </h3>
                <p className="text-sm text-gray-400 mt-1 max-w-xs mx-auto">
                  One quick SMS keeps the event feed spam-free. Verify your
                  number below to start browsing.
                </p>
              </div>
              <PhoneVerification
                embedded
                onVerified={() => setVerifyNonce((n) => n + 1)}
              />
            </div>
          )
          : error
          ? <div className="text-red-400 text-center py-12">{error}</div>
          : fetching && (viewMode === "swiped"
              ? (visibleEvents.length === 0 && visibleRecommended.length === 0)
              : visibleEvents.length === 0)
          ? (
            <div className="flex justify-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          )
          : (viewMode === "swiped"
              ? (visibleEvents.length === 0 && visibleRecommended.length === 0)
              : visibleEvents.length === 0)
          ? (
            <div className="text-gray-400 text-center py-12">
              {viewMode === "swiped"
                ? "You haven't saved any events yet."
                : "No events found in this area."}
            </div>
          )
          : viewMode === "swiped"
          ? (
            <div
              className={fetching
                ? "opacity-40 transition-opacity space-y-8"
                : "transition-opacity space-y-8"}
            >
              {visibleRecommended.length > 0 && (
                <div>
                  <h2 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2 tracking-wide uppercase">
                    ✨ Undecided + Recommended ({visibleRecommended.length})
                  </h2>
                  <div className="space-y-1">
                    {visibleRecommended.map((event) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        onSwipe={(dir) => handleSwipe(event.id, dir)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {likedFuture.length > 0 && (
                <div
                  className={visibleRecommended.length > 0
                    ? "border-t border-white/5 pt-4"
                    : ""}
                >
                  <h2 className="text-sm font-bold text-green-400/90 mb-3 flex items-center gap-2 tracking-wide uppercase">
                    💚 Saved (Upcoming) ({likedFuture.length})
                  </h2>
                  <div className="space-y-1">
                    {likedFuture.map((event) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        onSwipe={(dir) => handleSwipe(event.id, dir)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {likedPast.length > 0 && (
                <div className="border-t border-white/5 pt-4">
                  <button
                    type="button"
                    onClick={() => setLikedPastExpanded(!likedPastExpanded)}
                    className="w-full flex items-center justify-between text-left text-sm font-bold text-gray-400/90 mb-3 tracking-wide uppercase group hover:text-white transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      ⏳ Saved (Past) ({likedPast.length})
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-gray-500 group-hover:text-white transition-transform duration-200 ${
                        likedPastExpanded ? "" : "-rotate-90"
                      }`}
                    />
                  </button>
                  {likedPastExpanded && (
                    <div className="space-y-1">
                      {likedPast.map((event) => (
                        <EventRow
                          key={event.id}
                          event={event}
                          onSwipe={(dir) => handleSwipe(event.id, dir)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {disliked.length > 0 && (
                <div className="border-t border-white/5 pt-4">
                  <button
                    type="button"
                    onClick={() => setDislikedExpanded(!dislikedExpanded)}
                    className="w-full flex items-center justify-between text-left text-sm font-bold text-red-400/80 mb-3 tracking-wide uppercase group hover:text-red-300 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      💔 Passed ({disliked.length})
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-red-500/50 group-hover:text-red-300 transition-transform duration-200 ${
                        dislikedExpanded ? "" : "-rotate-90"
                      }`}
                    />
                  </button>
                  {dislikedExpanded && (
                    <div className="space-y-1">
                      {disliked.map((event) => (
                        <EventRow
                          key={event.id}
                          event={event}
                          onSwipe={(dir) => handleSwipe(event.id, dir)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
          : (
            <div
              className={fetching
                ? "opacity-40 transition-opacity"
                : "transition-opacity"}
            >
              {visibleEvents.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  onSwipe={(dir) => handleSwipe(event.id, dir)}
                />
              ))}
            </div>
          )}

        {fetching && (viewMode === "swiped"
          ? (visibleEvents.length > 0 || visibleRecommended.length > 0)
          : visibleEvents.length > 0) &&
          (
            <div className="absolute inset-0 flex items-center justify-center z-[999]">
              <Spinner className="h-8 w-8 text-purple-500" />
            </div>
          )}
      </div>

      {/* Toast Notification for Undo */}
      {showToast && lastSwipe && (
        <div className="fixed bottom-6 right-6 z-[9999] bg-slate-900 border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-fade-in duration-300">
          <span className="text-sm font-medium">
            {lastSwipe.direction === "like"
              ? (
                <>
                  Saved "{lastSwipe.eventName}" to{" "}
                  <span className="text-green-400 font-semibold">
                    Your Events
                  </span>
                </>
              )
              : (
                <>
                  Passed on "{lastSwipe.eventName}"
                </>
              )}
          </span>
          {lastSwipe.direction === "like" && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await api({
                    endpoint: "shareEventWithFriends",
                    payload: { eventId: lastSwipe.eventId },
                  });
                  setLastSwipe((prev) =>
                    prev
                      ? {
                        ...prev,
                        eventName: `${prev.eventName} (${res.message})`,
                      }
                      : null
                  );
                } catch (err: unknown) {
                  console.error("Failed to notify friends:", err);
                }
              }}
              className="ml-2 text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-3 py-1.5 rounded-lg transition-colors active:scale-95"
            >
              Notify Friends
            </button>
          )}
          <button
            type="button"
            onClick={handleUndoSwipe}
            className="ml-2 text-xs bg-purple-600 hover:bg-purple-500 text-white font-bold px-3 py-1.5 rounded-lg transition-colors active:scale-95"
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
};
